import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, from, Observable, of } from 'rxjs';
import { catchError, flatMap, map, shareReplay, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { CustomAlertService } from '../_components/custom-alert/custom-alert.service';
import { GoogleClass } from '../_models/googleClassroom/google-class';

const API_URL = environment.apiUrl;

@Injectable({
  providedIn: 'root'
})
export class ClassroomService {

  cambiosSubject = new BehaviorSubject<{obj: any, accion: string}>(null);
  cambios$ = this.cambiosSubject.asObservable().pipe(shareReplay(1));

  constructor(
    private http: HttpClient,
    private customAlert: CustomAlertService) { }

  enlazarClassroomMcp(materias: any[], accion: boolean, classroom: GoogleClass = null, eventos = []) {

    const model = {
      idOrigen: classroom ? classroom.id : 0,
      ids: materias.map(item => item.id).join(','),
      mensaje: '',
      estado: accion,
    };

    let request: Observable<any>;
    if (accion) {
      request = this.http.post<any>(`${API_URL}/api/Classroom/mcp/course`, model);
    } else {
      request = this.http.delete<any>(`${API_URL}/api/Classroom/mcp/${model.ids}/course`);
    }

    return request.pipe(
      tap(data => {
        this.cambiosSubject.next({obj:
          // Si la accion es enlazar (accion === true) se emite la respuesta del request, si es desenlazar se emite las materias con
          // classroomCourseId = null y course = null
          accion ? data : materias.map(item => {
            item.classroomCourseId = 0;
            item.course = null;
            return item;
          }),
          accion: 'materia-update'});
      })
    );
  }

  enlazarClassroomTarea(evento: any, accion: boolean, work: any = null) {
    if (work) {
      evento.courseWorkId = work.id || null;
      evento.courseWork = work;
      evento.courseId = work.courseId;
    }
    let request: Observable<any>;
    if (accion) {
      request = this.http.put<any>(`${API_URL}/api/Classroom/evento/coursework`, evento);
    } else {
      evento.courseWorkId = 0;
      request = this.http.delete<any>(`${API_URL}/api/Classroom/evento/${evento.id}/coursework`);
    }

    return request.pipe(
      tap(data => {
        // Cuando se hace delete (accion === false) se hace el update con el evento con el evento editado
        this.cambiosSubject.next({obj: accion ? data : evento, accion: 'evento-update'});
      })
    );
  }

  getClassroomClasses(): Observable<GoogleClass[]> {
    return this.http.get<GoogleClass[]>(`${API_URL}/api/Classroom/courses`);
  }

  classroomReturn(alumnos: any[], accion: string, mensaje: string, eventosIds): Observable<any[]> {
    // Se tienen que hacer 2 pedidos uno para los alumnos con classroom y otro para los alumnos sin.
    // Se crea un modelo para cada post y se combinan los dos requests para ser subscritos en el componente.
    const modelClassroom = {
      idsOrigen: eventosIds,
      ids: alumnos.filter(item => item.submission).map(item => item.id).toString(),
      msge: mensaje,
      estado: true
    };
    const modelSinClassroom = {
      idsOrigen: eventosIds,
      ids: alumnos.filter(item => item.submission === null || item.submission === undefined).map(item => item.id).toString(),
      msge: mensaje,
      estado: true
    };
    const requestClassroom = this.http.post<any>(`${API_URL}/api/classroom/studentSubmission/${accion}`, modelClassroom);
    const requestSinClassroom = this.http.post<any>(`${API_URL}/api/classroom/studentSubmission/return`, modelSinClassroom);
    return combineLatest([requestClassroom, requestSinClassroom]).pipe(
      map(([classroom, sinClassroom]) => {
        return classroom.concat(sinClassroom);
      }),
      tap(data => {
        this.cambiosSubject.next({obj: data, accion: 'alumnos-update'});
      })
    );
  }

  classroomReturnV2(alumnos: any[], accion: string,
    mensaje: string, unidadEventoMateriaId, estado, configuracion): Observable<any[]> {
    const model = {
      idOrigen: unidadEventoMateriaId,
      ids: alumnos.map(item => item.id).toString(),
      msge: mensaje,
      estado,
      configuracion
    };
    const requestSinClassroom = this.http.post<any>(`${API_URL}/api/v2/EventosAlumno/${accion}`, model);
    return requestSinClassroom.pipe(
      tap(data => {
        if (mensaje !== '') {
          alumnos.forEach(item => {
            item.comentarios = item.comentarios + 1;
          });
        }
        if (accion !== 'comentar') {
          const devolver = accion === 'publicar' ?  true : false;
          alumnos.forEach(item => {
            item.devuelto = devolver;
            item.eventoMateriaAlumnoConfig = configuracion;
          });
        }
        this.cambiosSubject.next({obj: data, accion: 'alumnos-update'});
      })
    );
  }

  recuperar(alumnos: any[], unidadEventoMateriaId): Observable<any[]> {
    const model = {
      idOrigen: unidadEventoMateriaId,
      ids: alumnos.map(item => item.id).toString(),
      msge: ''
    };
    const requestSinClassroom = this.http.post<any>(`${API_URL}/api/v2/EventosAlumno/devolver`, model);
    return requestSinClassroom.pipe(
      tap(data => {
        this.cambiosSubject.next({obj: data, accion: 'alumnos-update'});
      })
    );
  }

  getListaAsociacionMcp(ids: string) {
    return this.http.get<any[]>(`${API_URL}/api/Classroom/mcp/${ids}/course`);
  }

  getClassroomCourswork(id: string) {
    return this.http.get<any[]>(`${API_URL}/api/Classroom/course/${id}/courseWork`);
  }

  getStudentSubmissionsEvento(eventoId: number) {
    return this.http.get<any[]>(`${API_URL}/api/Classroom/evento/${eventoId}/courseWork/students`).pipe(
      catchError(err => {
        this.customAlert.setCustomMessage({
          tipo: 'error',
          texto: `Error al traer la información de google classroom. ${err.error} <br> Código de error: ${err.statusText}`
        });
        return of([]);
      })
    );
  }

  getStudentSubmission(eventoId: number, alumnoId: number) {
    return this.http.get<any>(`${API_URL}/api/Classroom/evento/${eventoId}/courseWork/students/${alumnoId}`).pipe(
      catchError(err => {
        this.customAlert.setCustomMessage({
          tipo: 'error',
          texto: `Error al traer la información de google classroom. ${err.error} <br> Código de error: ${err.statusText}`
        });
        return of(false);
      })
    );
  }

  crearEventoFechaMateria(evento: { id: number; duracion: number; isCompletado: boolean;
      fechaIsCompletado: string; fechaAplicar: string; fechaEntregaLimite: string;
      unidadEventoID: number; materiaCursoPeriodoID: any; }) {
    return this.http.post<any>(`${API_URL}/api/Classroom/evento/coursework`, evento);
  }
}
